import type { IAccount } from "@/modules/account/common/types";
import { TransferKeys } from "@/modules/transaction/common/enum";
import type { ITransactionAccount } from "@/shared/common/types";
import { isEqual, renderSelectedAccount } from "@/shared/helpers";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import type { TItemValue } from "@ab.uitools/ui-library/types/globalTypes";
import { useEffect, useMemo, useState } from "react";

interface IUseOwnAccountTransferParams {
  accountsList: IAccount[];
  isEdit?: boolean;
}

export const useOwnAccountTransfer = ({ accountsList, isEdit }: IUseOwnAccountTransferParams) => {
  const { setValue, setError, watch, errors = {}, reset } = useFormProps();

  const [creditAccountList, setCreditAccountList] = useState<IAccount[]>(accountsList);
  const [debitAccountList, setDebitAccountList] = useState<IAccount[]>(accountsList);
  const [editFilter, setEditFilter] = useState<{
    creditNumber: string;
    debitAccount?: ITransactionAccount;
  } | null>(null);

  const creditAccountsFilteredList = useMemo(() =>
    creditAccountList.map((item, index) => ({
      value: item.number,
      label: renderSelectedAccount(item),
      meta: { ...item, index }
    })), [creditAccountList]);

  const debitAccountsFilteredList = useMemo(() =>
    debitAccountList.map((item, index) => ({
      value: item.number,
      label: renderSelectedAccount(item),
      meta: { ...item, index }
    })), [debitAccountList]);

  const values = watch?.() || {};

  const handleClickDebitTarget = (value: TItemValue) => {
    const selectedCreditAccount = accountsList?.find(({ number }) => {
      return number === values.creditAccount;
    });

    const selectedDebitAccount = accountsList?.find(({ number }) => number === value);

    if (!!values.currency && values.currency !== selectedDebitAccount?.currency) {
      setValue(TransferKeys.Amount, "");
    }

    setValue(TransferKeys.Currency, selectedDebitAccount?.currency);

    setCreditAccountList(accountsList?.filter((
      { currency, number }) => {
      return number !== value && currency === selectedDebitAccount?.currency;
    }));

    if (selectedCreditAccount?.currency !== selectedDebitAccount?.currency) {
      setValue(TransferKeys.CreditAccount, "");
      setDebitAccountList(accountsList);
    }
  };

  const handleClickCreditTarget = (value: TItemValue) => {
    setDebitAccountList(accountsList?.filter(({ number }) => number !== value));
  };

  const handleSetCreditDebitAccountLists = (
    creditNumber: string,
    debitAccount?: ITransactionAccount
  ) => {
    setEditFilter({ creditNumber, debitAccount });
  };

  useEffect(() => {
    if (!isEdit && !isEqual(accountsList, creditAccountList)) {
      setCreditAccountList(accountsList);
    }
    if (!isEdit && !isEqual(accountsList, debitAccountList)) {
      setDebitAccountList(accountsList);
    }
  }, [accountsList, editFilter]);

  useEffect(() => {
    if (editFilter) {
      const selectedDebitAccountList = accountsList?.filter(
        ({ number }) => number !== editFilter.creditNumber
      );
      const selectedCreditAccountList = accountsList?.filter(({ number, currency }) => {
        return number !== editFilter.debitAccount?.number &&
          currency === editFilter.debitAccount?.currency;
      });
      setDebitAccountList(selectedDebitAccountList);
      setCreditAccountList(selectedCreditAccountList);
      return;
    }
  }, [editFilter, accountsList]);

  return {
    watch,
    reset,
    errors,
    values,
    setError,
    setValue,
    creditAccountsFilteredList,
    debitAccountsFilteredList,
    handleClickDebitTarget,
    handleClickCreditTarget,
    handleSetCreditDebitAccountLists,
  };
};
